import { Box, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import React, { useMemo } from 'react';
import { useSelector } from 'store/hooks';
import { CatalogWorkunit } from 'types/entities/CatalogWorkunit';
import { OrderStatus } from 'types/enums/OrderStatus';
import OrderWorkunit from 'types/entities/OrderWorkunit';
import Scope from 'types/entities/Scope';
import { SelectedWorkunit } from 'types/entities/SelectedWorkunit';
import useOrderWorkunitApi from '../../../services/useOrderWorkunitApi';
import './OrderAffectationScopeCell.scss';

type OrderAffectationScopeCellProps = {
  row: OrderWorkunit | CatalogWorkunit;
  disabled?: boolean;
  cellType: 'catalog' | 'selected';
};

const OrderAffectationScopeCell = (props: OrderAffectationScopeCellProps) => {
  const { row, disabled = false, cellType } = props;
  const state = useSelector((state) => state.catalogToOrder);
  const isProductionOrder = useSelector((state) => state.catalogToOrder.order?.status === OrderStatus.PRODUCTION);
  const isDisabled = disabled || isProductionOrder;
  const { changeWorkunitsScope, addScope } = useOrderWorkunitApi();

  const getRowId = (): number => {
    const isSelected = (r: any): r is SelectedWorkunit => 'randomId' in r;
    return isSelected(row) ? row.randomId ?? row.id : row.id;
  };

  const handleChange = async (event: SelectChangeEvent<number | string>) => {
    const { value } = event.target;
    const rowId = getRowId();

    if (value === '') {
      changeWorkunitsScope(null, rowId, cellType, true);
      return;
    }

    if (typeof value === 'string' && value.startsWith('__add__:')) {
      const name = value.replace('__add__:', '');
      const newScope = await addScope(name.trim());
      if (newScope) changeWorkunitsScope(newScope, rowId, cellType);
      return;
    }

    const scope = state.scopes.find((s) => s.id === Number(value));
    if (scope) changeWorkunitsScope(scope, rowId, cellType);
  };

  const filteredScopes: Scope[] = state.scopes.filter((s) => s.name !== 'not_attribued');

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
      <Select
            size="small"
            fullWidth
        displayEmpty
        disabled={isDisabled}
        value={row.scope?.id ?? ''}
        onChange={handleChange}
        onKeyDown={(e) => e.stopPropagation()}
        sx={{ fontSize: '.9rem' }}
      >
        <MenuItem value="">
          <span style={{ color: 'rgba(0,0,0,0.42)' }}>Scope</span>
        </MenuItem>
        {filteredScopes.map((scope) => (
          <MenuItem key={scope.id} value={scope.id}>
            {scope.name}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

export default (props: OrderAffectationScopeCellProps) =>
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(() => <OrderAffectationScopeCell {...props} />, [props.row?.scope]);
